import { SelectOption } from '@globaleris/ui.library'
import { EnumCommodity, EnumEstadoCliente, EnumTipoSujeto } from '../../api'
import {
  apiGetPostalAddressByPostalCode,
  apiGetPostalCodes
} from '../../api/Argentia/endpoints/addresses/addresses.api'
import { postalCodeListMapper } from '../../api/Argentia/endpoints/addresses/addresses.api.mapper'
import {
  apiFetchClientSubtypes,
  apiFetchCommodityTypes,
  apiFetchCountries,
  apiFetchDocumentTypes,
  apiFetchLanguage,
  apiFetchListAnnouncement,
  apiFetchListKpis,
  apiFetchMunicipalities,
  apiFetchNotificationTypes,
  apiFetchPersonTypes,
  apiFetchProvinces,
  apiFetchResidenceTypes,
  apiFetchSubjectsByTypeAndCommodity,
  apiGetClient,
  apiGetClientByDocument,
  apiGetSubjectFathers,
  apiGetSubjects,
  apiSaveClient,
  apiUpdateClient
} from '../../api/Argentia/endpoints/clients/clients.api'
import { MunicipalityModel, PostalAddress, ProvinceModel } from '../../api/Argentia/model/addresses'
import { enumValueToKey } from '../../helpers'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { SearchSelectOption } from '../../model/searchSelectOption'
import { ServiceResponse } from '../../model/serviceResponse'
import { getActiveBalanceZone, getActiveCompany } from '../../redux'
import {
  clientMapper,
  landingSubjectAnnouncementFilterMapper,
  landingSubjectKpisFilterMapper,
  subjectFilterMapper
} from './client.mapper'
import { ClientGeneralDataFormModel, SubjectModel } from './clients.model'

export const fetchClientSubtypes = async (
  subjectType: EnumTipoSujeto
): Promise<ServiceResponse<any>> => {
  return await apiFetchClientSubtypes(subjectType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPersonTypes = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchPersonTypes()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchDocumentTypes = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchDocumentTypes()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchLanguage = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchLanguage()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchNotificationTypes = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchNotificationTypes()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchMunicipalities = async (): Promise<
  ServiceResponse<MunicipalityModel[] | undefined>
> => {
  return await apiFetchMunicipalities()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchProvinces = async (): Promise<ServiceResponse<ProvinceModel[] | undefined>> => {
  return await apiFetchProvinces()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchCountries = async (): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apiFetchCountries()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchResidenceTypes = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchResidenceTypes()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchCommodityTypes = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchCommodityTypes()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchSubjectsByTypeAndCommodity = async (
  subjectType: EnumTipoSujeto,
  commodity: EnumCommodity
): Promise<ServiceResponse<any>> => {
  return await apiFetchSubjectsByTypeAndCommodity(subjectType, commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const saveClient = async (
  clientFormData: ClientGeneralDataFormModel
): Promise<ServiceResponse<any>> => {
  const activeCompany = getActiveCompany()
  const activeBalanceZone = getActiveBalanceZone()
  return await apiSaveClient(clientMapper(clientFormData, activeCompany!, activeBalanceZone!))
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchClient = async (
  clientId: number
): Promise<ServiceResponse<SubjectModel | undefined>> => {
  return await apiGetClient(clientId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateClient = async (
  clientFormData: ClientGeneralDataFormModel
): Promise<ServiceResponse<any>> => {
  const activeCompany = getActiveCompany()
  const activeBalanceZone = getActiveBalanceZone()
  return await apiUpdateClient(clientMapper(clientFormData, activeCompany!, activeBalanceZone!))
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchClients = async (
  search: string,
  subjectTypeList: EnumTipoSujeto[],
  isActive: boolean,
  commodity?: EnumCommodity
): Promise<ServiceResponse<any>> => {
  return await apiGetSubjects(subjectFilterMapper(search, subjectTypeList, isActive, commodity))
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchSubjectFathers = async (): Promise<
  ServiceResponse<SelectOption[] | undefined>
> => {
  return await apiGetSubjectFathers()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPostalCodes = async (query: string): Promise<SearchSelectOption[]> => {
  return await apiGetPostalCodes(query).then(response => {
    return postalCodeListMapper(response)
  })
  // .catch(error => {
  //   return createErrorServiceResponse(error)
  // })
}

export const getPostalAddressByID = async (postalCodeId: number): Promise<PostalAddress> => {
  return await apiGetPostalAddressByPostalCode(postalCodeId).then(response => {
    return response
  })
  // .catch(error => {
  //   return createErrorServiceResponse(error)
  // })
}

export const fetchListAnnouncement = async (
  subjectType: number,
  commodity?: EnumCommodity
): Promise<ServiceResponse<any>> => {
  const subjectTypesToFilter = [enumValueToKey(EnumTipoSujeto, subjectType)]
  const subjectStatesToFilter = [
    enumValueToKey(EnumEstadoCliente, EnumEstadoCliente.Aceptado),
    enumValueToKey(EnumEstadoCliente, EnumEstadoCliente.Activo)
  ]

  return await apiFetchListAnnouncement(
    landingSubjectAnnouncementFilterMapper(subjectTypesToFilter, subjectStatesToFilter, commodity)
  )
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchListKpis = async (
  subjectType: number,
  commodity?: EnumCommodity
): Promise<ServiceResponse<any>> => {
  const subjectTypesToFilter = [enumValueToKey(EnumTipoSujeto, subjectType)]

  return await apiFetchListKpis(landingSubjectKpisFilterMapper(subjectTypesToFilter, commodity))
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkDuplicates = async (
  document: string,
  id?: number,
  subjectType?: EnumTipoSujeto
): Promise<ServiceResponse<boolean | undefined>> => {
  const subjectTypeFilter = enumValueToKey(EnumTipoSujeto, subjectType)
  return await apiGetClientByDocument(document)
    .then(subjects => {
      let exists = false
      if (subjects.length > 0) {
        exists = id
          ? subjects.some(
              subject =>
                subject.id !== id &&
                subject.documentID === document &&
                subject.type === subjectTypeFilter
            )
          : subjects.some(
              subject => subject.documentID === document && subject.type === subjectTypeFilter
            )
      }
      return createSuccessServiceResponse(exists)
    })
    .catch(error => createErrorServiceResponse(error))
}
